import {
  coerceDateTimeLocal,
  snowflakeId,
  toApiDateTime,
} from '../journey-accommodation/accommodationModel';
import { coerceDateTime } from '../my-journeys/myJourneysModel';

export const DECISION_STATUSES = Object.freeze(['DRAFT', 'OPEN', 'CLOSED', 'CANCELLED']);
export const DECISION_TYPES = Object.freeze(['POLL', 'ACTION_DECISION', 'CONTINGENCY']);
export const ELIGIBILITY_RULES = Object.freeze(['ACTIVE_JOURNEY_MEMBERS', 'OWNER_COORDINATORS_ONLY']);

export const DECISION_STATUS_LABELS = Object.freeze({
  DRAFT: 'Draft',
  OPEN: 'Open for voting',
  CLOSED: 'Resolved',
  CANCELLED: 'Cancelled',
});

export const DECISION_TYPE_LABELS = Object.freeze({
  POLL: '投票',
  ACTION_DECISION: '行動決策',
  CONTINGENCY: '臨時狀況',
});

export const WRITABLE_JOURNEY_LIFECYCLES = Object.freeze(['READY', 'TRAVELING', 'COMPLETED']);

export function decisionStatusLabel(status) {
  return DECISION_STATUS_LABELS[String(status || '')] || String(status || '—');
}

export function decisionTypeLabel(type) {
  return DECISION_TYPE_LABELS[String(type || '')] || String(type || '—');
}

export function classifyDecisionError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  if (code === 90003 || code === 90005 || code === 90006
      || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (code === 92001 || key === 'travel.journey.not.found') return 'journey-not-found';
  if (key.includes('decision.not.found') || (code === 94002 && key.includes('decision'))) return 'not-found';
  if (key.includes('option.not.found')) return 'not-found';
  if (key.includes('not.eligible')) return 'not-eligible';
  if (key.includes('permission.denied')) return 'forbidden';
  if (key.includes('concurrent.modification')) return 'conflict';
  if (key.includes('idempotency.conflict')) return 'idempotency-conflict';
  if (key.includes('vote.change.forbidden')) return 'vote-change-forbidden';
  if (key.includes('voting.closed')) return 'voting-closed';
  if (key.includes('invalid.lifecycle') || key.includes('lifecycle.not.writable')) return 'lifecycle';
  if (code === 94000 || key.includes('validation')) return 'validation';
  if (code === 94001) return 'forbidden';
  if (code === 94002) return 'not-found';
  if (code === 94003) return 'conflict';
  return 'error';
}

export function createDecisionClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `dec-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

export function normalizeActorBallot(value) {
  if (value == null) return null;
  return {
    ballotId: snowflakeId(value.ballotId),
    optionId: value.optionId == null ? null : snowflakeId(value.optionId),
    abstained: Boolean(value.abstained),
    castAt: coerceDateTime(value.castAt),
    updatedAt: coerceDateTime(value.updatedAt),
  };
}

export function normalizeDecisionOption(value) {
  return {
    optionId: snowflakeId(value?.optionId),
    label: String(value?.label || ''),
    description: value?.description == null ? '' : String(value.description),
    displayOrder: Number(value?.displayOrder) || 0,
    voteCount: Number(value?.voteCount) || 0,
  };
}

export function normalizeDecisionListItem(value) {
  const status = String(value?.decisionStatus || '');
  return {
    decisionId: snowflakeId(value?.decisionId),
    journeyId: snowflakeId(value?.journeyId),
    title: String(value?.title || ''),
    decisionType: String(value?.decisionType || ''),
    decisionTypeLabel: decisionTypeLabel(value?.decisionType),
    decisionStatus: status,
    decisionStatusLabel: decisionStatusLabel(status),
    createdByMemberId: snowflakeId(value?.createdByMemberId),
    openedAt: coerceDateTime(value?.openedAt),
    deadlineAt: coerceDateTime(value?.deadlineAt),
    selectedOptionId: value?.selectedOptionId == null ? null : snowflakeId(value.selectedOptionId),
    rowVersion: value?.rowVersion == null ? null : String(value.rowVersion),
    actorEligible: Boolean(value?.actorEligible),
    actorHasVoted: Boolean(value?.actorHasVoted),
    participationCount: Number(value?.participationCount) || 0,
    optionCount: Number(value?.optionCount) || 0,
    createTime: coerceDateTime(value?.createTime),
    updateTime: coerceDateTime(value?.updateTime),
    isDraft: status === 'DRAFT',
    isOpen: status === 'OPEN',
    isClosed: status === 'CLOSED',
    isCancelled: status === 'CANCELLED',
  };
}

export function normalizeDecisionList(payload) {
  if (!Array.isArray(payload)) return [];
  return payload.map(normalizeDecisionListItem);
}

export function normalizeJourneyDecision(value) {
  const status = String(value?.decisionStatus || '');
  const options = Array.isArray(value?.options)
    ? [...value.options].map(normalizeDecisionOption).sort((a, b) => a.displayOrder - b.displayOrder)
    : [];
  return {
    decisionId: snowflakeId(value?.decisionId),
    journeyId: snowflakeId(value?.journeyId),
    createdByMemberId: snowflakeId(value?.createdByMemberId),
    situationReferenceId: value?.situationReferenceId == null ? null : snowflakeId(value.situationReferenceId),
    title: String(value?.title || ''),
    description: value?.description == null ? '' : String(value.description),
    decisionType: String(value?.decisionType || ''),
    decisionTypeLabel: decisionTypeLabel(value?.decisionType),
    decisionStatus: status,
    decisionStatusLabel: decisionStatusLabel(status),
    votingMethod: String(value?.votingMethod || 'SINGLE_CHOICE'),
    eligibilityRule: String(value?.eligibilityRule || ''),
    allowAbstain: Boolean(value?.allowAbstain),
    allowVoteChange: Boolean(value?.allowVoteChange),
    minimumParticipationCount: value?.minimumParticipationCount == null
      ? null
      : Number(value.minimumParticipationCount),
    openedAt: coerceDateTime(value?.openedAt),
    deadlineAt: coerceDateTime(value?.deadlineAt),
    closedAt: coerceDateTime(value?.closedAt),
    closedByMemberId: value?.closedByMemberId == null ? null : snowflakeId(value.closedByMemberId),
    selectedOptionId: value?.selectedOptionId == null ? null : snowflakeId(value.selectedOptionId),
    cancellationReason: value?.cancellationReason == null ? '' : String(value.cancellationReason),
    rowVersion: value?.rowVersion == null ? null : String(value.rowVersion),
    options,
    eligibleCount: Number(value?.eligibleCount) || 0,
    participationCount: Number(value?.participationCount) || 0,
    abstainCount: Number(value?.abstainCount) || 0,
    tie: value?.tie == null ? null : Boolean(value.tie),
    participationThresholdMet: value?.participationThresholdMet == null
      ? null
      : Boolean(value.participationThresholdMet),
    actorEligible: Boolean(value?.actorEligible),
    actorBallot: normalizeActorBallot(value?.actorBallot),
    optionVoteCounts: Array.isArray(value?.optionVoteCounts)
      ? value.optionVoteCounts.map((row) => ({
        optionId: snowflakeId(row?.optionId),
        voteCount: Number(row?.voteCount) || 0,
        displayOrder: Number(row?.displayOrder) || 0,
      }))
      : [],
    replay: value?.replay == null ? null : Boolean(value.replay),
    createTime: coerceDateTime(value?.createTime),
    updateTime: coerceDateTime(value?.updateTime),
    isDraft: status === 'DRAFT',
    isOpen: status === 'OPEN',
    isClosed: status === 'CLOSED',
    isCancelled: status === 'CANCELLED',
  };
}

export function normalizeDecisionResults(value) {
  return {
    decisionId: snowflakeId(value?.decisionId),
    journeyId: snowflakeId(value?.journeyId),
    decisionStatus: String(value?.decisionStatus || ''),
    eligibleCount: Number(value?.eligibleCount) || 0,
    participationCount: Number(value?.participationCount) || 0,
    abstainCount: Number(value?.abstainCount) || 0,
    optionVoteCounts: Array.isArray(value?.optionVoteCounts)
      ? value.optionVoteCounts.map((row) => ({
        optionId: snowflakeId(row?.optionId),
        voteCount: Number(row?.voteCount) || 0,
        displayOrder: Number(row?.displayOrder) || 0,
      }))
      : [],
    selectedOptionId: value?.selectedOptionId == null ? null : snowflakeId(value.selectedOptionId),
    tie: value?.tie == null ? null : Boolean(value.tie),
    participationThresholdMet: value?.participationThresholdMet == null
      ? null
      : Boolean(value.participationThresholdMet),
    calculatedAt: coerceDateTime(value?.calculatedAt),
    actorBallot: normalizeActorBallot(value?.actorBallot),
  };
}

export function partitionDecisionList(items) {
  const active = [];
  const history = [];
  for (const item of items || []) {
    if (item.decisionStatus === 'DRAFT' || item.decisionStatus === 'OPEN') active.push(item);
    else history.push(item);
  }
  return { active, history };
}

export function canCreateDecision({ role, lifecycleStatus }) {
  if (!WRITABLE_JOURNEY_LIFECYCLES.includes(lifecycleStatus)) return false;
  return role === 'OWNER' || role === 'COORDINATOR' || role === 'MEMBER';
}

export function canManageDecision({ role, lifecycleStatus, createdByMemberId, viewerMemberId }) {
  if (!WRITABLE_JOURNEY_LIFECYCLES.includes(lifecycleStatus)) return false;
  if (role === 'OWNER' || role === 'COORDINATOR') return true;
  return String(createdByMemberId) === String(viewerMemberId);
}

export function emptyDecisionDraft() {
  return {
    title: '',
    description: '',
    decisionType: 'CONTINGENCY',
    eligibilityRule: 'ACTIVE_JOURNEY_MEMBERS',
    allowAbstain: true,
    allowVoteChange: true,
    minimumParticipationCount: '',
    deadlineAt: '',
    options: [
      { optionId: null, label: '', description: '', displayOrder: 1 },
      { optionId: null, label: '', description: '', displayOrder: 2 },
    ],
  };
}

export function draftFromDecision(decision) {
  return {
    title: decision?.title || '',
    description: decision?.description || '',
    decisionType: decision?.decisionType || 'POLL',
    eligibilityRule: decision?.eligibilityRule || 'ACTIVE_JOURNEY_MEMBERS',
    allowAbstain: Boolean(decision?.allowAbstain),
    allowVoteChange: Boolean(decision?.allowVoteChange),
    minimumParticipationCount: decision?.minimumParticipationCount == null
      ? ''
      : String(decision.minimumParticipationCount),
    deadlineAt: coerceDateTimeLocal(decision?.deadlineAt),
    options: (decision?.options || []).map((row, index) => ({
      optionId: row.optionId,
      label: row.label || '',
      description: row.description || '',
      displayOrder: row.displayOrder || index + 1,
    })),
  };
}

function buildOptionPayload(options, { includeOptionId = false } = {}) {
  return (options || []).map((row, index) => {
    const item = {
      label: String(row.label || '').trim(),
      description: row.description == null || String(row.description).trim() === ''
        ? null
        : String(row.description).trim(),
      displayOrder: Number(row.displayOrder) || index + 1,
    };
    if (includeOptionId) {
      // Keep Snowflake IDs as strings — never Number() coerce opaque identities.
      item.optionId = row.optionId == null || row.optionId === '' ? null : String(row.optionId);
    }
    return item;
  });
}

export function buildCreateDecisionPayload(draft, { clientRequestId } = {}) {
  const minParticipation = String(draft?.minimumParticipationCount || '').trim();
  return {
    clientRequestId: String(clientRequestId || '').trim(),
    situationReferenceId: null,
    title: String(draft?.title || '').trim(),
    description: draft?.description == null || String(draft.description).trim() === ''
      ? null
      : String(draft.description).trim(),
    decisionType: draft?.decisionType,
    eligibilityRule: draft?.eligibilityRule || 'ACTIVE_JOURNEY_MEMBERS',
    allowAbstain: Boolean(draft?.allowAbstain),
    allowVoteChange: Boolean(draft?.allowVoteChange),
    minimumParticipationCount: minParticipation ? Number(minParticipation) : null,
    deadlineAt: toApiDateTime(draft?.deadlineAt),
    options: buildOptionPayload(draft?.options, { includeOptionId: false }),
    confirm: true,
  };
}

export function buildUpdateDraftPayload(draft, { expectedDecisionRowVersion } = {}) {
  const minParticipation = String(draft?.minimumParticipationCount || '').trim();
  return {
    expectedDecisionRowVersion: Number(expectedDecisionRowVersion),
    title: String(draft?.title || '').trim(),
    description: draft?.description == null || String(draft.description).trim() === ''
      ? null
      : String(draft.description).trim(),
    decisionType: draft?.decisionType,
    eligibilityRule: draft?.eligibilityRule || 'ACTIVE_JOURNEY_MEMBERS',
    allowAbstain: Boolean(draft?.allowAbstain),
    allowVoteChange: Boolean(draft?.allowVoteChange),
    minimumParticipationCount: minParticipation ? Number(minParticipation) : null,
    deadlineAt: toApiDateTime(draft?.deadlineAt),
    options: buildOptionPayload(draft?.options, { includeOptionId: true }),
    confirm: true,
  };
}

export function buildOpenPayload({ expectedDecisionRowVersion } = {}) {
  return {
    expectedDecisionRowVersion: Number(expectedDecisionRowVersion),
    confirm: true,
  };
}

export function buildVotePayload({
  expectedDecisionRowVersion,
  optionId = null,
  abstained = false,
} = {}) {
  return {
    expectedDecisionRowVersion: Number(expectedDecisionRowVersion),
    optionId: abstained || optionId == null || optionId === '' ? null : String(optionId),
    abstained: Boolean(abstained),
    confirm: true,
  };
}

export function buildClosePayload({ expectedDecisionRowVersion } = {}) {
  return {
    expectedDecisionRowVersion: Number(expectedDecisionRowVersion),
    confirm: true,
  };
}

export function buildCancelPayload({ expectedDecisionRowVersion, reason } = {}) {
  return {
    expectedDecisionRowVersion: Number(expectedDecisionRowVersion),
    reason: String(reason || '').trim(),
    confirm: true,
  };
}

export function validateDecisionDraft(draft) {
  const title = String(draft?.title || '').trim();
  if (!title) return 'Title is required.';
  if (title.length > 100) return 'Title must be at most 100 characters.';
  if (!DECISION_TYPES.includes(draft?.decisionType)) return 'Decision type is required.';
  if (!ELIGIBILITY_RULES.includes(draft?.eligibilityRule)) return 'Eligibility rule is required.';
  const options = draft?.options || [];
  if (options.length < 2) return 'At least two options are required.';
  const labels = new Set();
  for (const row of options) {
    const label = String(row.label || '').trim();
    if (!label) return 'Each option needs a label.';
    if (label.length > 100) return 'Option labels must be at most 100 characters.';
    if (labels.has(label)) return 'Option labels must be unique.';
    labels.add(label);
  }
  const min = String(draft?.minimumParticipationCount || '').trim();
  if (min && (!/^\d+$/.test(min) || Number(min) < 1)) {
    return 'Minimum participation must be a positive integer.';
  }
  return '';
}

export function isDeadlinePassed(deadlineAt, now = new Date()) {
  if (!deadlineAt) return false;
  const text = String(deadlineAt);
  const normalized = text.includes('T') ? text : text.replace(' ', 'T');
  const ms = Date.parse(normalized);
  if (Number.isNaN(ms)) return false;
  return ms <= now.getTime();
}

export function ownBallotSummary(decision) {
  const ballot = decision?.actorBallot;
  if (!ballot) return '尚未投票';
  if (ballot.abstained) return '你目前選擇棄權';
  const option = (decision?.options || []).find((row) => String(row.optionId) === String(ballot.optionId));
  return option ? `你目前選擇：${option.label}` : '你目前已投票';
}

export { coerceDateTimeLocal, snowflakeId, toApiDateTime };
